import { useEffect, useMemo, useRef } from 'react';

type EffectResult =
  | void
  | undefined
  | (() => EffectResult)
  | Promise<void | undefined | (() => EffectResult)>;

/**
 * In some cases it may be necessary to mock dismissErrorWithoutHandling
 * in tests.
 */
export function dismissErrorWithoutHandling(error: Error) {
  setTimeout(() => {
    throw error;
  }, 0);
}

function createRethrowAsync() {
  const syncStackError = new Error(
    `Encountered unexpected rejection in async side effect`
  );

  return (error: Error) => {
    dismissErrorWithoutHandling(error);
    dismissErrorWithoutHandling(syncStackError);
  };
}

async function handleEffectResult(effectResult: EffectResult): Promise<void> {
  if (effectResult instanceof Promise) {
    return handleEffectResult(await effectResult);
  } else if (typeof effectResult === 'function') {
    return handleEffectResult(effectResult());
  }
  return undefined;
}

export function useAsyncEffect(effect: () => EffectResult, deps: unknown[]) {
  const rethrowAsync = useMemo(createRethrowAsync, []);
  const { current: asyncEffectState } = useRef({
    mostRecentEffect: effect,
    asyncQueue: Promise.resolve(undefined as EffectResult),
    done: false,
  });

  asyncEffectState.mostRecentEffect = effect;

  useEffect(() => {
    const newLink = handleEffectResult(asyncEffectState.asyncQueue)
      .catch(rethrowAsync)
      .then(() => {
        // we've reached the end of the queue, so it's time to trigger work
        if (!asyncEffectState.done && asyncEffectState.asyncQueue === newLink) {
          return asyncEffectState.mostRecentEffect();
        } else {
          return undefined;
        }
      })
      .catch(rethrowAsync);
    asyncEffectState.asyncQueue = newLink;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asyncEffectState, rethrowAsync, ...deps]);

  useEffect(
    () => () => {
      asyncEffectState.done = true;
      handleEffectResult(asyncEffectState.asyncQueue).catch(rethrowAsync);
    },
    [asyncEffectState, rethrowAsync]
  );
}
